import React, {useCallback, useState} from 'react';

import {
  Plugins,
  withSystem,
  withMeta,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  useExpressionEditorContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TRecord,
  TTypeConstructor,
  TText,
  TListOfElement,
  TEnum,
  TBoolean,
  TNumber,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {styled, styledDiv, emotionStyled} from '@packages/utilities/styled';
import useSliderControls, {type Slide} from './useSliderControls';
import {
  EnhancedDropdown,
  Option,
} from '@packages/systems/design-system/Dropdown';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  ANIMATION_PATH,
  AUTO_MAX_PATH,
  AUTOPLAY_PATH,
  DELAY_PATH,
  DISABLE_SWIPE_PATH,
  DURATION_PATH,
  EASING_PATH,
  HIDE_ARROWS_PATH,
  INFINITE_PATH,
  NAV_INVERT_PATH,
  NAV_NUMBERS_PATH,
  NAV_ROUND_PATH,
  NAV_SHADOW_PATH,
  NAV_SPACING_PATH,
} from '../constants';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {
  AddIcon,
  Button,
  CheckDefaultIcon,
  ChevronSmallDownIcon,
  ChevronSmallLeftIcon,
  ChevronSmallRightIcon,
  Divider,
  ElementSliderDefaultIcon,
  HStack,
  IconButton,
  Menu,
  MenuItem,
  MenuContent,
  MenuSmallChevronIcon,
  MenuTrigger,
  Text,
  Tooltip,
  View,
} from '@packages/systems/spring';
import {
  type ContextualExpressionExpr,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  MiniToolbarButtons,
  useMiniToolbarInstanceProxy,
  selectPendingMiniToolbarAction,
  clearPendingEditAction,
} from '@packages/systems/designer/ui';
import {selectIsOnSecondaryLocale} from '@packages/systems/localization';
import {useTranslation} from '@packages/systems/designer-translations';

const {getIn} = ContextualExpressions;

const reverseLabelBoolean = qualify(
  Plugins.name('Basic'),
  typeName('ReverseLabelBoolean')
);
const MSNumber = qualify(Plugins.name('Basic'), typeName('MSNumber'));

const slider = {
  animation: withMeta(
    {variant: 'menu', withoutLivePreview: true},
    withSystem(
      {
        label: 'Animation',
        labelWidth: 'narrowest',
        defaultValue: 'slide',
        isLocalizable: true,
      },
      TEnum({
        slide: 'Slide',
        cross: 'Cross Fade',
        outin: 'Fade Out - In',
        fade: 'Fade Over',
        over: 'Slide Over',
      })
    )
  ),
  easing: withSystem(
    {label: 'Easing', defaultValue: 'ease', isLocalizable: true},
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('EasingsEnum')))
  ),
  duration: withSystem(
    {
      defaultValue: 500,
      label: 'Duration',
      labelWidth: 'narrow',
      isLocalizable: true,
    },
    TTypeConstructor(MSNumber)
  ),
  infinite: withSystem(
    {
      label: 'Infinite repeat slides',
      labelVariant: 'hidden',
      defaultValue: true,
      isLocalizable: true,
    },
    TTypeConstructor(reverseLabelBoolean)
  ),
  disableSwipe: withSystem(
    {
      label: 'Disable swipe gestures',
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(reverseLabelBoolean)
  ),
  autoplay: withSystem(
    {label: 'Auto-play slides', labelVariant: 'hidden', isLocalizable: true},
    TTypeConstructor(reverseLabelBoolean)
  ),
  delay: withSystem(
    {
      defaultValue: 4000,
      label: 'Timer delay',
      isLocalizable: true,
    },
    TTypeConstructor(MSNumber)
  ),
  autoMax: withSystem(
    {
      label: 'Stop after',
      isLocalizable: true,
    },
    TNumber
  ),
  hideArrows: withSystem(
    {
      label: 'Hide arrows at each end',
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(reverseLabelBoolean)
  ),
  navRound: withSystem(
    {
      label: 'Rounded',
      defaultValue: true,
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(reverseLabelBoolean)
  ),
  navNumbers: withSystem(
    {label: 'Number labels', labelVariant: 'hidden', isLocalizable: true},
    TTypeConstructor(reverseLabelBoolean)
  ),
  navShadow: withSystem(
    {label: 'Shadow', labelVariant: 'hidden', isLocalizable: true},
    TTypeConstructor(reverseLabelBoolean)
  ),
  navInvert: withSystem(
    {label: 'Invert colors', labelVariant: 'hidden', isLocalizable: true},
    TTypeConstructor(reverseLabelBoolean)
  ),
  navSpacing: withSystem(
    {
      label: 'Spacing',
      defaultValue: 3,
      isLocalizable: true,
    },
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('PXNumber')))
  ),
  iconArrows: withSystem({private: true, defaultValue: true}, TBoolean),
  type: withSystem({private: true, defaultValue: 'wrapper'}, TText),
} as const;

const sliderWrapperDataFields = {
  slider: TRecord(slider),
  children: TListOfElement,
} as const;

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
const ControlButton = (props) => {
  return (
    <Hitarea>
      <Button variant="ghost" {...props} />
    </Hitarea>
  );
};

const ControlButtonWithGap = styled(ControlButton)(({theme: {gutters}}) => ({
  gap: gutters.base,
}));

const AddSlide = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => (
  <ControlButtonWithGap
    disabled={disabled}
    onClick={onClick}
    data-automation-id="SliderSettingsAddSlide"
  >
    <AddIcon size={12} />
    Add Slide
  </ControlButtonWithGap>
);

// @ts-expect-error - TS7031 - Binding element 'onClick' implicitly has an 'any' type.
const PreviousSlide = ({onClick}) => (
  <ControlButton
    onClick={onClick}
    data-automation-id="SliderSettingsPreviousArrow"
  >
    <ChevronSmallLeftIcon />
  </ControlButton>
);

// @ts-expect-error - TS7031 - Binding element 'onClick' implicitly has an 'any' type.
const NextSlide = ({onClick}) => (
  <ControlButton onClick={onClick} data-automation-id="SliderSettingsNextArrow">
    <ChevronSmallRightIcon />
  </ControlButton>
);

// @ts-expect-error - TS7031 - Binding element 'slides' implicitly has an 'any' type. | TS7031 - Binding element 'onSelect' implicitly has an 'any' type.
const SlideDropDown = ({slides, onSelect}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onChange = (index: any) => {
    onSelect(slides[index].id);
    setIsOpen(false);
  };
  const button = () => (
    <ControlButton open={isOpen} onClick={() => setIsOpen(!isOpen)}>
      <MenuSmallChevronIcon />
    </ControlButton>
  );

  return (
    <EnhancedDropdown
      handleChange={onChange}
      value={slides[0].id}
      button={button}
      width={120}
      maxHeight={400}
      hideOverlay
      open={isOpen}
    >
      {/* @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type. */}
      {slides.map((option, index) => (
        <Option key={option.id} value={index} {...option} />
      ))}
    </EnhancedDropdown>
  );
};

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: '$2',
});

const GridTwoByTwo = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
});

const Title = emotionStyled('div')({
  display: 'grid',
  marginTop: '$1',
});

const LeftButtons = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 44px',
  gap: '$1',
});

const SliderButtons = styledDiv(({theme: {gutters, control}}) => ({
  height: control.height,
  display: 'grid',
  gridTemplateColumns: '92px 1fr',
  marginBottom: gutters.narrow,
  gap: gutters.normal,
}));

const DarkBox = emotionStyled('div')({
  padding: '$1 $2',
  background: '$uiCardBackground',
  borderRadius: '$2',
});

const DarkBoxBottomPadding = emotionStyled(DarkBox)({
  padding: '$1 $2 $2',
});

const CompactSliderControls = ({
  slides: allSlides,
  currentSlideId,
  onSelectSlide,
  toPrevSlide,
  toNextSlide,
}: {
  slides: Slide[];
  currentSlideId: string;
  onSelectSlide: (id: string) => void;
  toPrevSlide: () => void;
  toNextSlide: () => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const currentSlideIndex = allSlides.findIndex((s) => s.id === currentSlideId);

  const currentSlideName =
    currentSlideIndex !== -1
      ? `Slide ${currentSlideIndex + 1}`
      : 'Select slide';

  return (
    <HStack padding="$1">
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} maxHeight={400}>
        <MenuTrigger
          prefix={ElementSliderDefaultIcon}
          suffix={ChevronSmallDownIcon}
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
        >
          {currentSlideName}
        </MenuTrigger>
        <MenuContent>
          {allSlides.map((slide, index) => (
            <MenuItem
              key={slide.id}
              prefix={
                index === currentSlideIndex ? (
                  <CheckDefaultIcon colorVariant="primary" />
                ) : (
                  <View width="$4" />
                )
              }
              onClick={() => {
                onSelectSlide(slide.id);
              }}
            >
              <Text textTransform="capitalize">{slide.label}</Text>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      <View paddingY="$1" alignSelf="stretch" display="flex">
        <Divider orientation="vertical" elevation={1} />
      </View>
      <IconButton
        Icon={ChevronSmallLeftIcon}
        onClick={toPrevSlide}
        variant="ghost"
        label="Previous slide"
      />
      <IconButton
        Icon={ChevronSmallRightIcon}
        onClick={toNextSlide}
        variant="ghost"
        label="Next slide"
      />
    </HStack>
  );
};

const isOnSecondaryLocaleSelector = (state: DesignerStores) =>
  selectIsOnSecondaryLocale(state.LocalizationStore);

const SliderWrapperType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: sliderWrapperDataFields,
  render: function SliderWrapperRender({expr, dispatch}) {
    const eeContext = useExpressionEditorContext();
    const t = useTranslation();
    const wfDispatch = useDispatch();
    const instanceProxyContext = useMiniToolbarInstanceProxy();
    const pendingMiniToolbarAction = useDesignerSelector(
      selectPendingMiniToolbarAction
    );
    // Tree structure changes are not allowed in secondary locales
    const isOnSecondaryLocale = useDesignerSelector(
      isOnSecondaryLocaleSelector
    );

    const hasPendingSliderAction =
      pendingMiniToolbarAction?.buttonType ===
      MiniToolbarButtons.SLIDER_NAVIGATION;

    const {
      toPrevSlide,
      toNextSlide,
      immediatelyToSlide,
      addSlide,
      slides,
      currentSlideId,
    } = useSliderControls(expr, dispatch, {
      suppressMountAnimation: hasPendingSliderAction,
    });

    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    // a helper function to pass nested expression to ExpressionEditor and (optionally) return value of expression
    const expressionEditorAtPath = useCallback(
      (
        path: ExpressionPath,
        getValue: void | ((arg1: ContextualExpressionExpr) => any)
      ) => {
        const subExpr = getIn(expr, path);
        if (!subExpr)
          throw Error(
            `failed to get expression at path: ${JSON.stringify(path)}`
          );
        let val = null;
        if (getValue) {
          const strippedAnn = stripAnn(subExpr);
          val = getValue(strippedAnn);
        }
        const Editor = (
          <ExpressionEditor expression={subExpr} dispatch={dispatchAt(path)} />
        );
        return [Editor, val];
      },
      [expr, dispatchAt]
    );

    const Children = (
      <SliderButtons>
        <Tooltip
          label={t('designer.localization.disabled.tooltip')}
          isDisabled={!isOnSecondaryLocale}
        >
          <AddSlide onClick={addSlide} disabled={isOnSecondaryLocale} />
        </Tooltip>
        <LeftButtons>
          <PreviousSlide onClick={toPrevSlide} />
          <NextSlide onClick={toNextSlide} />
          {slides.length > 0 && (
            <SlideDropDown
              slides={slides}
              // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
              onSelect={(id) => immediatelyToSlide(id)}
            />
          )}
        </LeftButtons>
      </SliderButtons>
    );

    const [Duration] = expressionEditorAtPath(DURATION_PATH);
    const [Animation, animationENum] = expressionEditorAtPath(
      ANIMATION_PATH,
      ContextualExpressions.getEnum
    );
    const isSlideAnimation = animationENum === 'slide';
    const [Easing] = expressionEditorAtPath(EASING_PATH);
    const [Infinite] = expressionEditorAtPath(INFINITE_PATH);
    const [DisableSwipe] = expressionEditorAtPath(DISABLE_SWIPE_PATH);
    const [Autoplay, hasAutoplay] = expressionEditorAtPath(
      AUTOPLAY_PATH,
      ContextualExpressions.getBoolean
    );
    const [Delay] = expressionEditorAtPath(DELAY_PATH);
    const [AutoMax] = expressionEditorAtPath(AUTO_MAX_PATH);
    const [HideArrows] = expressionEditorAtPath(HIDE_ARROWS_PATH);
    const [NavRound] = expressionEditorAtPath(NAV_ROUND_PATH);
    const [NavNumbers] = expressionEditorAtPath(NAV_NUMBERS_PATH);
    const [NavShadow] = expressionEditorAtPath(NAV_SHADOW_PATH);
    const [NavInvert] = expressionEditorAtPath(NAV_INVERT_PATH);
    const [NavSpacing] = expressionEditorAtPath(NAV_SPACING_PATH);

    const AutoplaySettings = hasAutoplay ? (
      <>
        <DarkBox>
          {isSlideAnimation && Infinite}
          {DisableSwipe}
          {Autoplay}
        </DarkBox>
        {Delay}
        {AutoMax}
      </>
    ) : (
      <DarkBoxBottomPadding>
        {isSlideAnimation && Infinite}
        {DisableSwipe}
        {Autoplay}
      </DarkBoxBottomPadding>
    );

    // After focusing into a component from an unfocused instance, detect the
    // pending SLIDER_NAVIGATION action, clear it, and replay the stored slide action.
    // This mirrors the Navbar pattern in NavbarWrapperType.tsx.
    React.useEffect(() => {
      if (
        pendingMiniToolbarAction?.buttonType ===
        MiniToolbarButtons.SLIDER_NAVIGATION
      ) {
        wfDispatch(clearPendingEditAction());
        const metadata = pendingMiniToolbarAction.metadata;
        if (metadata?.type === 'prev') toPrevSlide();
        else if (metadata?.type === 'next') toNextSlide();
        else if (metadata?.type === 'immediate')
          immediatelyToSlide(metadata.slideId);
      }
    }, [
      pendingMiniToolbarAction,
      wfDispatch,
      toPrevSlide,
      toNextSlide,
      immediatelyToSlide,
    ]);

    if (eeContext === 'compact') {
      if (slides.length === 0) {
        return null;
      }

      const handlePrevSlide = () => {
        if (instanceProxyContext) {
          instanceProxyContext.dispatchPendingAction({type: 'prev'});
        } else {
          toPrevSlide();
        }
      };

      const handleNextSlide = () => {
        if (instanceProxyContext) {
          instanceProxyContext.dispatchPendingAction({type: 'next'});
        } else {
          toNextSlide();
        }
      };

      const handleSelectSlide = (id: string) => {
        if (instanceProxyContext) {
          instanceProxyContext.dispatchPendingAction({
            type: 'immediate',
            slideId: id,
          });
        } else {
          immediatelyToSlide(id);
        }
      };

      return (
        <CompactSliderControls
          slides={slides}
          currentSlideId={currentSlideId}
          onSelectSlide={handleSelectSlide}
          toPrevSlide={handlePrevSlide}
          toNextSlide={handleNextSlide}
        />
      );
    }

    return (
      <Grid>
        {Children}
        {Animation}
        {Easing}
        {Duration}
        {AutoplaySettings}
        <Title>Arrows</Title>
        <DarkBox>{HideArrows}</DarkBox>
        <Title>Slide Nav</Title>
        <DarkBox>
          <GridTwoByTwo>
            {NavRound}
            {NavNumbers}
            {NavShadow}
            {NavInvert}
          </GridTwoByTwo>
        </DarkBox>
        {NavSpacing}
      </Grid>
    );
  },
});

export default SliderWrapperType;
